import { EyeOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';

const PREVIEW_MASK_CLASS = 'preview-image-mask';

const useStyles = createStyles(({ token }) => ({
  previewable: {
    position: 'relative',
    display: 'inline-flex',
    flexShrink: 0,
    cursor: 'pointer',
    borderRadius: token.borderRadius,
    overflow: 'hidden',
    [`&:hover .${PREVIEW_MASK_CLASS}`]: {
      opacity: 1,
    },
  },
  previewMask: {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.45)',
    color: token.colorTextLightSolid,
    fontSize: 18,
    opacity: 0,
    transition: `opacity ${token.motionDurationMid}`,
    pointerEvents: 'none',
  },
  image: {
    display: 'block',
    objectFit: 'cover',
  },
}));

export function openImagePreview(url?: string) {
  if (!url) {
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export interface PreviewImageProps {
  src?: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: React.CSSProperties;
  imageStyle?: React.CSSProperties;
  previewable?: boolean;
  children?: React.ReactNode;
}

const PreviewImage: React.FC<PreviewImageProps> = ({
  src,
  alt,
  width,
  height,
  className,
  style,
  imageStyle,
  previewable = true,
  children,
}) => {
  const { token } = theme.useToken();
  const { styles, cx } = useStyles();

  const handlePreview = () => {
    openImagePreview(src);
  };

  if (children) {
    if (!previewable || !src) {
      return <>{children}</>;
    }

    return (
      <span
        className={cx(styles.previewable, className)}
        style={style}
        onClick={handlePreview}
      >
        {children}
        <span className={cx(styles.previewMask, PREVIEW_MASK_CLASS)}>
          <EyeOutlined />
        </span>
      </span>
    );
  }

  if (!src) {
    return null;
  }

  const imageNode = (
    <img
      src={src}
      alt={alt}
      className={cx(styles.image, className)}
      style={{
        width,
        height,
        backgroundColor: token.colorPrimaryBg,
        ...style,
        ...imageStyle,
      }}
    />
  );

  if (!previewable) {
    return imageNode;
  }

  return (
    <span
      className={cx(styles.previewable, className)}
      style={{ width, height, ...style }}
      onClick={handlePreview}
    >
      {imageNode}
      <span className={cx(styles.previewMask, PREVIEW_MASK_CLASS)}>
        <EyeOutlined />
      </span>
    </span>
  );
};

export default PreviewImage;
